/**
 * The store module responsible for the Online Shop:
 * 1 - it holds the state of the shop's cart; to access it there are 4 getters:
 * 1.1 - the getter which returns the Items on the cart;
 * 1.2 - the getter which returns the Number of Items on the cart;
 * 1.3 - the getter which returns the Total Amount of the cart.
 * 1.4 - the getter which indicates if the cart contains Items with no stock
 * 1.5 - the getter which indicates if the cart contains Items of the type Menu
 * 2 - the shop cart is mutate using the 4 implemented actions:
 * 2.1 - adding Items to the cart;
 * 2.2 - removing Items from the cart;
 * 2.3 - deleting Items from the cart.
 * 2.4 - setting the shop cart to an empty array
 * 3 - it holds the state of the Delivery Options; to access it there is one getter:
 * 3.1 - the getter which returns the complete Delivery Options definitions: method key, method description and
 *  pickup store
 * 4 - the Delivery Options is mutate using the 2 implemented actions:
 * 4.1 - setting the chosen Delivery method;
 * 4.2 - removing the Delivery Options definitions;
 */
import {itemsService} from '@/_services';

export const shop = {
    namespaced: true,
    state: {
        cart: [],
        delivery: {},
    },
    mutations: {
        /**
         * Setting the store with the Cart's definition which is stored on the Local Storage
         * @param state
         */
        initialiseStore: (state) => {
            if (localStorage.getItem('shop.cart')) {
                state.cart = JSON.parse(localStorage.getItem('shop.cart'));
            }
            if (localStorage.getItem('shop.delivery')) {
                state.delivery = JSON.parse(localStorage.getItem('shop.delivery'));
            }
        },
        /**
         * Adding a new Item to the Shopping Cart, which is stored on the local storage
         * @param state
         * @param item: the Item's identifier
         */
        addToCart: (state, item) => {
            // Find the item in the cart list
            let cartProduct = state.cart.find((cartItem) => cartItem.item_id === item.id);

            if (cartProduct) {
                // Item already present in the cart, so increase the quantity
                cartProduct.quantity++;
            } else {
                state.cart.push({
                    // Item newly added to cart
                    "item_id": item.id,
                    "item_code": item.code,
                    "item_name": item.name,
                    "item_unit_price": item.price,
                    "item_image": item.images[0],
                    quantity: 1,
                    "stock": item.stock,
                    "menu_items": []
                });

            }
            // Setting the new state of the cart to the Local Storage
            localStorage.setItem('shop.cart', JSON.stringify(state.cart));
        },
        addMenuToCart: (state, item) => {
            state.cart.push({
                // Item newly added to cart
                "item_id": item.id,
                "item_code": item.code,
                "item_name": item.name,
                "item_unit_price": item.price,
                "item_image": item.images[0],
                quantity: 1,
                "stock": item.stock,
                "menu_items": item.menu_items
            });

            // Setting the new state of the cart to the Local Storage
            localStorage.setItem('shop.cart', JSON.stringify(state.cart));
        },
        /**
         * A unit of the given Item is removed from the Shopping Cart. If the final quantity of the Item is zero,
         * the entrance of the Item is completely removed from the Shopping Cart.
         * @param state
         * @param item: the Item's identifier
         *
         * @deprecated
         */
        removeFromCart: (state, item) => {
            // Find the item in the cart list
            let cartProduct = state.cart.find((cartItem) => cartItem.item_id === item.id);
            // Find the item index in the cart list
            let cartProductIndex = state.cart.findIndex((cartItem) => cartItem.item_id === item.id);

            // Decrementing the Item's quantity
            cartProduct.quantity--;

            // Deleting the Item from the cart since its quantity is Zero
            if (cartProduct.quantity <= 0) {
                state.cart.splice(cartProductIndex, 1);
            } else {
                // Setting the new state of the cart to the Local Storage
                localStorage.setItem('shop.cart', JSON.stringify(state.cart));
            }
        },
        /**
         * The given Item is excluded from the Shopping Cart.
         * @param state
         * @param item: the Item's identifier
         * @param cartPosition: the position of the Item on the Shopping Cart's array
         */
        deleteFromCart: (state, {item, cartPosition}) => {
            // Find the item index in the cart list
            if (!cartPosition) {
                let cartProductIndex = state.cart.findIndex((cartItem) => cartItem.item_id === item.id);

                // Remove the item from the cart
                state.cart.splice(cartProductIndex, 1);
            } else {
                // The way of identifying the item to remove from the Shopping Cart is now based on the given position.
                // The method changed due to the implementation of the Menus: on the Shopping Cart it is possible to have
                // more than an Item with the same identifier
                if (state.cart[cartPosition].item_id === item.id) {
                    // Remove the item from the cart
                    state.cart.splice(cartPosition, 1);

                    // Setting the new state of the cart to the Local Storage
                    localStorage.setItem('shop.cart', JSON.stringify(state.cart));
                }
            }

            // Setting the new state of the cart to the Local Storage
            localStorage.setItem('shop.cart', JSON.stringify(state.cart));
        },
        /**
         * Setting the store with the cart's definition which is stored on the Local Storage
         * @param state
         * @param cart: an array of Items. Each Item's definition includes: item_id, item_code, item_name,
         *  item_unit_price, item_image and quantity
         */
        setShopCart: (state, cart) => {
            if (localStorage.getItem('shop.cart')) {
                state.cart = cart;

                // Setting the new state of the cart to the Local Storage
                localStorage.setItem('shop.cart', JSON.stringify(state.cart));
            }
        },
        /**
         * Removing the Cart's Items which were stored on the Local Storage
         * @param state
         */
        setShopCartEmpty: (state) => {
            if (localStorage.getItem('shop.cart')) {
                state.cart = [];

                // Setting the new state of the cart to the Local Storage
                localStorage.setItem('shop.cart', JSON.stringify(state.cart));
            }
        },
        /**
         * Setting the store with the selected Store to collect the Order on the Local Storage
         * @param state
         * @param methodKey
         * @param methodTitle
         * @param shop
         */
        setDeliveryOptions: (state, {methodKey, methodTitle, shop}) => {
            state.delivery = {
                "method_title": methodTitle,
                "method_key": methodKey,
                "store": shop,
            };

            // Setting the new state of the Delivery Options to the Local Storage
            localStorage.setItem('shop.delivery', JSON.stringify(state.delivery));
        },
        /**
         * Removing the Delivery Options which were stored on the Local Storage
         * @param state
         */
        setDeliveryOptionsEmpty: (state) => {
            if (localStorage.getItem('shop.delivery')) {
                state.delivery = {};

                // Setting the new state of the Delivery Options to the Local Storage
                localStorage.setItem('shop.delivery', JSON.stringify(state.delivery));
            }
        },
    },
    actions: {
        addToCart: ({commit, dispatch}, itemId) => {
            itemsService.get(itemId).then((item) => {
                    commit("addToCart", item);
                    // Dispatching from the root context, since the Alert is from another module
                    dispatch('alert/success', '$vuetify.shop.shop_item_added_to_cart', {root: true})
                },
                // eslint-disable-next-line
                error => {
                    dispatch('alert/error', '$vuetify.generic_error', {root: true});
                });
        },
        addMenuToCart: ({commit, dispatch}, {itemId, itemsIdMenu}) => {
            itemsService.getMenuItems(itemId, itemsIdMenu).then((item) => {
                    commit("addMenuToCart", item);
                    // Dispatching from the root context, since the Alert is from another module
                    dispatch('alert/success', '$vuetify.shop.shop_item_added_to_cart', {root: true})
                },
                // eslint-disable-next-line
                error => {
                    dispatch('alert/error', '$vuetify.generic_error', {root: true});
                });
        },
        /**
         * @param commit
         * @param dispatch
         * @param itemId
         *
         * @deprecated
         */
        removeFromCart: ({commit, dispatch}, itemId) => {
            itemsService.get(itemId).then((item) => {
                    commit("removeFromCart", item);
                    // Dispatching from the root context, since the Alert is from another module
                    dispatch('alert/error', '$vuetify.shop.shop_item_remove_from_cart', {root: true})
                },
                // eslint-disable-next-line
                error => {
                    dispatch('alert/error', '$vuetify.generic_error', {root: true});
                });
        },
        deleteFromCart: ({commit, dispatch}, {itemId, cartPosition}) => {
            itemsService.get(itemId).then((item) => {
                    commit("deleteFromCart", {item, cartPosition});
                    // Dispatching from the root context, since the Alert is from another module
                    dispatch('alert/success', '$vuetify.shop.shop_item_remove_from_cart', {root: true})
                },
                // eslint-disable-next-line
                error => {
                    dispatch('alert/error', '$vuetify.generic_error', {root: true});
                });

        },
        addListOfItemsToCart: ({commit, dispatch}, listItemsId) => {
            for (var i = 0; i < listItemsId.length; i++) {
                itemsService.get(listItemsId[i]).then((item) => {
                        commit("addToCart", item);
                        // Dispatching from the root context, since the Alert is from another module
                        dispatch('alert/success', '$vuetify.shop.shop_item_added_to_cart', {root: true})
                    },
                    // eslint-disable-next-line
                    error => {
                        dispatch('alert/error', '$vuetify.generic_error');
                    });
            }
        },
        setShopCart: ({commit}, shopCart) => {
            commit("setShopCart", shopCart);
        },
        setShopCartEmpty: ({commit}) => {
            commit("setShopCartEmpty");
        },
        setDeliveryOptions: ({commit}, {methodKey, methodTitle, shop}) => {
            commit("setDeliveryOptions", {methodKey, methodTitle, shop});
        },
        setDeliveryOptionsEmpty: ({commit}) => {
            commit("setDeliveryOptionsEmpty");
        },
    },
    getters: {
        cartSize: (state) => {
            return state.cart.length;
        },
        cartTotalAmount: (state) => {
            return state.cart.reduce((total, item) => {
                return total + (item.item_unit_price * item.quantity);
            }, 0).toFixed(2);
        },
        cartItems: (state) => {
            //Items should be ordered by the Items' name
            return state.cart.sort((a, b) => (a.item_name > b.item_name) ? 1 : -1);
        },
        cartItemsNoStock: (state) => {
            for (var i = 0; i < state.cart.length; i++) {
                if (state.cart[i].stock === 0) {
                    return 1;
                }
            }

            return 0;
        },
        cartHasItemMenu: (state) => {
            for (var i = 0; i < state.cart.length; i++) {
                if (state.cart[i].menu_items && Object.entries(state.cart[i].menu_items).length > 0) {
                    return 1;
                }
            }

            return 0;
        },
        deliveryInfo: (state) => {
            return state.delivery;
        },
    },
};
